import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { authAPI } from '../features/auth/services/api';
import { copyrightLine } from '../config/brand';
import '../styles/Auth.css';

const OTP_LENGTH = 6;

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const emailFromQuery = searchParams.get('email') || '';

  const [email, setEmail] = useState(emailFromQuery);
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
  const [status, setStatus] = useState(emailFromQuery ? 'input' : 'ask-email'); // ask-email | input | verifying | success | error
  const [message, setMessage] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const inputRefs = useRef([]);
  const timerRef = useRef(null);

  const startCooldown = useCallback(() => {
    setCooldown(30);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  // Auto-focus first input when entering OTP mode
  useEffect(() => {
    if (status === 'input' && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [status]);

  const handleOtpChange = (index, value) => {
    // Only accept digits
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setMessage('');

    // Auto-focus next input
    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (value && index === OTP_LENGTH - 1 && newOtp.every(d => d !== '')) {
      handleVerify(newOtp.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (pasted.length === 0) return;
    const newOtp = Array(OTP_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i++) newOtp[i] = pasted[i];
    setOtp(newOtp);
    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();
    if (pasted.length === OTP_LENGTH) handleVerify(pasted);
  };

  const handleVerify = async (otpCode) => {
    const code = otpCode || otp.join('');
    if (code.length !== OTP_LENGTH) {
      setMessage('Please enter the complete 6-digit code.');
      return;
    }
    setStatus('verifying');
    try {
      const response = await authAPI.verifyEmail(email, code);
      setStatus('success');
      setMessage(response.data.message || 'Email verified successfully!');
    } catch (err) {
      setStatus('input');
      setOtp(Array(OTP_LENGTH).fill(''));
      setMessage(err.response?.data?.detail || 'Invalid or expired OTP. Please try again.');
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResendMessage('');
    try {
      await authAPI.resendVerification(email);
      setResendMessage('A new code has been sent to your email.');
      setOtp(Array(OTP_LENGTH).fill(''));
      startCooldown();
    } catch {
      setResendMessage('Failed to resend. Please try again.');
    }
    setResending(false);
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (email.trim()) {
      setStatus('input');
    }
  };

  // ── Ask for email (if not provided in query) ──────────────────
  if (status === 'ask-email') {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="verification-icon">
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                <circle cx="32" cy="32" r="32" fill="#667eea" opacity="0.1"/>
                <rect x="16" y="22" width="32" height="22" rx="3" stroke="#667eea" strokeWidth="2.5" fill="none"/>
                <path d="M16 25l16 12 16-12" stroke="#667eea" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            </div>
            <h1>Verify Your Email</h1>
            <p>Enter the email you registered with</p>
          </div>
          <form onSubmit={handleEmailSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                autoComplete="email"
                autoFocus
              />
            </div>
            <button type="submit" className="auth-button">
              Continue
            </button>
          </form>
          <div className="auth-footer">
            <p><Link to="/login">← Back to Login</Link></p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="verify-email-page">
      <div className="verify-email-card">

        {/* ── Verifying ─────────────────────────────────────── */}
        {status === 'verifying' && (
          <>
            <div className="verify-email-icon">
              <div className="spinner-large"></div>
            </div>
            <h1 className="verify-email-title">Verifying…</h1>
            <p className="verify-email-subtitle">Please wait a moment.</p>
          </>
        )}

        {/* ── OTP Input ─────────────────────────────────────── */}
        {status === 'input' && (
          <>
            <div className="verify-email-icon">
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                <circle cx="28" cy="28" r="28" fill="#667eea" opacity="0.12"/>
                <rect x="14" y="20" width="28" height="18" rx="3" stroke="#667eea" strokeWidth="2.5" fill="none"/>
                <path d="M14 23l14 10 14-10" stroke="#667eea" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            </div>
            <h1 className="verify-email-title">Enter Verification Code</h1>
            <p className="verify-email-subtitle">
              We sent a 6-digit code to<br />
              <strong style={{ color: '#2d3748' }}>{email}</strong>
            </p>

            <div className="otp-input-group" onPaste={handlePaste}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={el => inputRefs.current[index] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className="otp-input"
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  autoComplete="one-time-code"
                />
              ))}
            </div>

            {message && (
              <p className="otp-error-message">{message}</p>
            )}

            <button
              className="verify-email-btn"
              onClick={() => handleVerify()}
              disabled={otp.some(d => d === '')}
            >
              Verify Email
            </button>

            <div className="otp-resend-section">
              <p className="verify-email-hint">Didn't receive the code? Check spam folder.</p>
              <button
                className="otp-resend-btn"
                onClick={handleResend}
                disabled={resending || cooldown > 0}
              >
                {resending ? 'Sending…' : cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend Code'}
              </button>
              {resendMessage && (
                <p className="otp-resend-message">{resendMessage}</p>
              )}
            </div>
          </>
        )}

        {/* ── Success ───────────────────────────────────────── */}
        {status === 'success' && (
          <>
            <div className="verify-email-icon verify-email-icon--success">
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                <circle cx="28" cy="28" r="28" fill="#38a169" opacity="0.12"/>
                <path d="M18 28l7 7 13-13" stroke="#38a169" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h1 className="verify-email-title">Email Verified!</h1>
            <p className="verify-email-subtitle">{message}</p>
            <Link to="/login" state={{ verified: true }} className="verify-email-btn">
              Go to Login
            </Link>
          </>
        )}

        {/* ── Error ─────────────────────────────────────────── */}
        {status === 'error' && (
          <>
            <div className="verify-email-icon verify-email-icon--error">
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                <circle cx="28" cy="28" r="28" fill="#e53e3e" opacity="0.12"/>
                <path d="M20 20l16 16M36 20L20 36" stroke="#e53e3e" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            </div>
            <h1 className="verify-email-title">Verification Failed</h1>
            <p className="verify-email-subtitle">{message}</p>
            <Link to="/login" className="verify-email-btn">
              Go to Login
            </Link>
          </>
        )}

        <div className="verify-email-footer">
          {copyrightLine(2026)}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
